import React, { useRef, useState, useEffect } from "react";
import api from "@/lib/api";
import { Upload, FileCheck2, Trash2, Download, ChevronDown, ChevronUp, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import OrderMessages from "@/components/OrderMessages";
import { DOC_CATEGORIES, categoryMeta, backendDocUrl } from "@/lib/docCategories";
import { ApplicantBreakdown } from "@/pages/ApplyReview";
import { applicantIsComplete, applicantDisplayName, formatDestinationSummary } from "@/lib/applicants";
import { worldCountryByCode } from "@/lib/worldCountries";
import { ADD_ONS_BY_ID } from "@/lib/addons";
import { Flag } from "@/components/Flag";

const STATUSES = [
  { value: "pending_payment", label: "Pending Payment" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
];

export default function AdminOrderDetail({ order, onUpdate }) {
  const [uploading, setUploading] = useState(false);

  const updateStatus = async (status) => {
    try {
      await api.patch(`/admin/orders/${order.id}/status`, { status });
      toast.success("Status updated");
      onUpdate();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const [markingPaid, setMarkingPaid] = useState(false);
  const markPaymentReceived = async () => {
    if (order.payment_status === "paid") return;
    const ok = window.confirm(
      "This will mark the order as paid and notify the customer — are you sure?\n\n" +
      "Use this only when Stripe (or another payment provider) confirmed the payment but our system never received the callback."
    );
    if (!ok) return;
    setMarkingPaid(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/mark-paid`);
      if (data?.already_paid) {
        toast.info("Order was already marked as paid.");
      } else {
        toast.success("Payment marked as received. Confirmation emails queued.");
      }
      onUpdate();
    } catch (err) {
      console.error("mark-paid failed", err?.response?.status, err?.response?.data);
      toast.error("Could not mark as paid. Please try again.");
    } finally {
      setMarkingPaid(false);
    }
  };

  const uploadFiles = async (files, category, applicantIndex) => {
    if (!files || !files.length || !category) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("category", category);
        if (applicantIndex !== null && applicantIndex !== undefined && applicantIndex !== "")
          fd.append("applicant_index", String(applicantIndex));
        await api.post(`/admin/orders/${order.id}/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`${files.length} file(s) uploaded as ${category}`);
      onUpdate();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const deleteDoc = async (path) => {
    if (!confirm("Delete this document?")) return;
    try {
      await api.delete(`/admin/orders/${order.id}/documents`, { params: { path } });
      toast.success("Document removed");
      onUpdate();
    } catch {
      toast.error("Failed to remove");
    }
  };

  const openDoc = (index) => {
    window.open(backendDocUrl(order.id, index), "_blank");
  };

  // Group documents by applicant_index (or "unassigned" / general)
  const groupedDocs = (() => {
    const groups = new Map();
    (order.documents || []).forEach((d, i) => {
      const key = (typeof d.applicant_index === "number") ? d.applicant_index : "general";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push({ ...d, _idx: i });
    });
    return groups;
  })();

  const applicants = order.applicants || [];

  return (
    <div className="bg-[#f8fafc] border-t border-[#e2e8f0] p-6 space-y-6" data-testid={`order-detail-${order.id}`}>
      {/* Add-ons badges */}
      {(order.add_ons || []).length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid={`order-addons-${order.id}`}>
          <span className="text-xs uppercase tracking-widest text-[#64748b]">Add-ons:</span>
          {order.add_ons.map((id) => {
            const a = ADD_ONS_BY_ID[id]; if (!a) return null;
            return (
              <span key={id} data-testid={`addon-badge-${id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full bg-[#eff6ff] text-[#2563eb] border border-[#bfdbfe]">
                {a.short} (+${a.price})
              </span>
            );
          })}
        </div>
      )}

      {/* Applicants accordion */}
      <ApplicantsSection
        applicants={applicants}
        numExpected={order.num_applicants}
        orderId={order.id}
        onUpdate={onUpdate}
        spainUsaEligible={
          (order.applying_from_country || "").toUpperCase() === "US" &&
          (order.destination_country || "").toLowerCase() === "spain"
        }
      />

      {/* Add-on details accordion */}
      {(order.add_ons || []).length > 0 && (
        <AddOnDetailsSection addOns={order.add_ons} details={order.add_on_details || {}} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Customer + Order info */}
      <div className="space-y-5 lg:col-span-1">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#64748b] mb-2">Customer</div>
          <div className="bg-white border border-[#e2e8f0] rounded-md p-4 space-y-1.5 text-sm">
            <div><span className="text-[#64748b]">Name: </span><span className="text-[#0f1b2d] font-medium">{order.full_name}</span></div>
            <div><span className="text-[#64748b]">Email: </span><span className="text-[#0f1b2d]">{order.email}</span></div>
            <div><span className="text-[#64748b]">WhatsApp: </span>
              <a href={`https://wa.me/${(order.whatsapp || "").replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer" className="text-[#2563eb] font-medium hover:underline">{order.whatsapp}</a>
            </div>
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-[#64748b] mb-2">Order</div>
          <div className="bg-white border border-[#e2e8f0] rounded-md p-4 space-y-1.5 text-sm" data-testid={`order-info-${order.id}`}>
            <div><span className="text-[#64748b]">Destination: </span><span className="text-[#0f1b2d]">{formatDestinationSummary(order)}</span></div>
            {order.nationality && (() => {
              const c = worldCountryByCode(order.nationality);
              return (
                <div data-testid={`order-nationality-${order.id}`}>
                  <span className="text-[#64748b]">Nationality: </span>
                  {c ? (
                    <span className="text-[#0f1b2d] inline-flex items-center gap-1.5 align-middle">
                      <Flag code={c.code} />
                      <span>{c.name} ({order.nationality})</span>
                    </span>
                  ) : (
                    <span className="text-[#0f1b2d]">{order.nationality}</span>
                  )}
                </div>
              );
            })()}
            {(order.applying_from_country || order.applying_from_city) && (() => {
              const c = worldCountryByCode(order.applying_from_country);
              const city = (order.applying_from_city || "").trim();
              return (
                <div data-testid={`order-applying-from-${order.id}`}>
                  <span className="text-[#64748b]">Applying from: </span>
                  <span className="text-[#0f1b2d] inline-flex items-center gap-1.5 align-middle">
                    {city && <span>{city},</span>}
                    {c && <Flag code={c.code} />}
                    <span>{c ? `${c.name} (${order.applying_from_country})` : (order.applying_from_country || "")}</span>
                  </span>
                </div>
              );
            })()}
            <div><span className="text-[#64748b]">Applicants: </span><span className="text-[#0f1b2d]">{order.num_applicants}</span></div>
            <div><span className="text-[#64748b]">Package: </span><span className="text-[#0f1b2d]">{order.package_name} (${Math.round(order.amount)})</span></div>
            {order.order_type === "custom" && Array.isArray(order.selected_documents) && order.selected_documents.length > 0 && (
              <div className="pt-2 border-t border-dashed border-[#e2e8f0]" data-testid="admin-custom-file-details">
                <div className="text-[10px] uppercase tracking-widest text-[#64748b] font-semibold mb-1">Custom File Details</div>
                <ul className="space-y-0.5">
                  {(order.price_breakdown || []).map((row, i) => (
                    <li key={i} className="flex items-center justify-between gap-3 text-[#0f1b2d]">
                      <span className="truncate">• {row.name}</span>
                      <span className="text-[#475569] flex-shrink-0">${row.price}</span>
                    </li>
                  ))}
                </ul>
                <CustomFormDataBlock order={order} />
              </div>
            )}
            <div><span className="text-[#64748b]">Delivery: </span><span className="text-[#0f1b2d] capitalize">{order.delivery_type === "express" ? "Express (3 hours)" : "Normal (24 hours)"}</span></div>
            <div className="flex items-center gap-2">
              <span className="text-[#64748b]">Payment: </span>
              <span className="text-[#0f1b2d] capitalize">{order.payment_status}</span>
              {order.payment_status !== "paid" && (
                <button
                  type="button"
                  onClick={markPaymentReceived}
                  disabled={markingPaid}
                  data-testid={`mark-paid-${order.id}`}
                  className="ml-2 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  title="Manually record payment (use only when Stripe callback was missed)"
                >
                  {markingPaid ? "Marking…" : "Mark payment received"}
                </button>
              )}
              {order.manually_marked_paid_by && (
                <span className="ml-2 text-[11px] text-[#64748b] italic" title={`Manually marked paid by ${order.manually_marked_paid_by}`}>
                  (manual — {order.manually_marked_paid_by})
                </span>
              )}
            </div>
            {order.payment_status === "paid" && order.delivery_type === "express" && (order.paid_at || order.updated_at) && (() => {
              const paidD = new Date(order.paid_at || order.updated_at);
              const dlD = new Date(paidD.getTime() + 3 * 3600000);
              const t = (d) => d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
              const day = paidD.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
              return (
                <div data-testid={`order-paid-at-${order.id}`}>
                  <span className="text-[#64748b]">Paid at: </span>
                  <span className="text-[#0f1b2d]">{t(paidD)}, {day}</span>
                  <span className="text-[#64748b]"> · Express deadline: </span>
                  <span className="font-semibold text-amber-700">{t(dlD)}</span>
                </div>
              );
            })()}
            {order.notes && <div className="pt-2 border-t border-[#e2e8f0] text-[#475569] italic">&ldquo;{order.notes}&rdquo;</div>}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-[#64748b] mb-2">Update status</div>
          <select
            value={order.status}
            onChange={(e) => updateStatus(e.target.value)}
            data-testid={`detail-status-${order.id}`}
            className="w-full text-sm border border-[#e2e8f0] rounded-md px-3 py-2 bg-white outline-none focus:border-[#2563eb]"
          >
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </div>

      {/* Documents — per-applicant + general */}
      <div className="lg:col-span-1">
        <div className="text-xs uppercase tracking-widest text-[#64748b] mb-2">Documents (per applicant)</div>
        <div className="space-y-3">
          {applicants.map((a, i) => (
            <ApplicantDocGroup
              key={i}
              orderId={order.id}
              applicantIndex={i}
              applicantLabel={`Applicant ${i + 1} — ${applicantDisplayName(a) || "(no name)"}`}
              docs={groupedDocs.get(i) || []}
              uploading={uploading}
              onUpload={uploadFiles}
              onDelete={deleteDoc}
              onOpen={openDoc}
            />
          ))}
          <ApplicantDocGroup
            orderId={order.id}
            applicantIndex={null}
            applicantLabel="General / shared documents"
            docs={groupedDocs.get("general") || []}
            uploading={uploading}
            onUpload={uploadFiles}
            onDelete={deleteDoc}
            onOpen={openDoc}
          />
        </div>
      </div>

      {/* Messages */}
      <div className="lg:col-span-1">
        <OrderMessages orderId={order.id} role="admin" compact />
      </div>
      </div>
    </div>
  );
}

function ApplicantDocGroup({ applicantIndex, applicantLabel, docs, uploading, onUpload, onDelete, onOpen }) {
  const fileRef = useRef(null);
  const [category, setCategory] = useState(DOC_CATEGORIES[0]?.value || "Other");
  const testKey = applicantIndex === null ? "general" : `app-${applicantIndex}`;
  return (
    <div className="bg-white border border-[#e2e8f0] rounded-md" data-testid={`doc-group-${testKey}`}>
      <div className="px-3 py-2.5 border-b border-[#e2e8f0] bg-[#f8fafc] flex items-center justify-between gap-2">
        <div className="text-sm font-semibold text-[#0f1b2d] truncate">{applicantLabel}</div>
        <span className="text-[10px] uppercase tracking-wider text-[#64748b]">{docs.length} doc{docs.length === 1 ? "" : "s"}</span>
      </div>
      <div className="p-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid={`upload-category-${testKey}`}
            className="text-xs border border-[#e2e8f0] rounded-md px-2 py-1.5 bg-white outline-none focus:border-[#2563eb] flex-1 min-w-[140px]"
          >
            {DOC_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.emoji} {c.value}</option>)}
          </select>
          <button
            type="button"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            data-testid={`upload-btn-${testKey}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#2563eb] hover:bg-[#1d4ed8] disabled:opacity-60 text-white px-3 py-1.5 rounded-md"
          >
            <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Upload"}
          </button>
          <input
            ref={fileRef}
            type="file"
            hidden
            multiple
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={async (e) => {
              const files = Array.from(e.target.files || []);
              await onUpload(files, category, applicantIndex);
              if (fileRef.current) fileRef.current.value = "";
            }}
          />
        </div>
        {docs.length === 0 ? (
          <div className="text-xs text-[#94a3b8] italic text-center py-3">No documents yet</div>
        ) : (
          <div className="space-y-1.5">
            {docs.map((d) => {
              const cat = categoryMeta(d.category);
              return (
                <div key={d._idx} className="flex items-center gap-2 p-2 rounded-md hover:bg-[#f8fafc]" data-testid={`doc-row-${testKey}-${d._idx}`}>
                  <FileCheck2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#eff6ff] text-[#2563eb] text-[11px] font-semibold flex-shrink-0">
                    <span>{cat.emoji}</span><span>{cat.value}</span>
                  </span>
                  <span className="flex-1 text-xs text-[#0f1b2d] truncate">{d.original_filename}</span>
                  <button onClick={() => onOpen(d._idx)} data-testid={`doc-download-${testKey}-${d._idx}`} className="p-1.5 rounded hover:bg-[#eff6ff] text-[#2563eb]" title="Download">
                    <Download className="w-4 h-4" />
                  </button>
                  <button onClick={() => onDelete(d.storage_path)} data-testid={`doc-delete-${testKey}-${d._idx}`} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function SpainHotelBlock({ applicant, orderId, applicantIdx, onUpdate }) {
  const [values, setValues] = useState({
    hotel_name: applicant.hotel_name || "",
    hotel_address: applicant.hotel_address || "",
    hotel_phone: applicant.hotel_phone || "",
  });
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // Keep local state in sync when the applicant prop changes (e.g. after
  // parent onUpdate refresh).
  useEffect(() => {
    setValues({
      hotel_name: applicant.hotel_name || "",
      hotel_address: applicant.hotel_address || "",
      hotel_phone: applicant.hotel_phone || "",
    });
  }, [applicant.hotel_name, applicant.hotel_address, applicant.hotel_phone]);

  const anySet = values.hotel_name || values.hotel_address || values.hotel_phone;
  const dirty =
    values.hotel_name !== (applicant.hotel_name || "") ||
    values.hotel_address !== (applicant.hotel_address || "") ||
    values.hotel_phone !== (applicant.hotel_phone || "");

  const save = async (e) => {
    e.stopPropagation();
    setSaving(true);
    try {
      await api.patch(
        `/admin/orders/${orderId}/applicants/${applicantIdx}/hotel`,
        values,
      );
      toast.success("Hotel details saved");
      onUpdate && onUpdate();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save hotel details");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="rounded-md border border-[#e2e8f0] bg-[#f8fafc]" data-testid={`spain-hotel-block-${applicantIdx}`}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
        className="w-full flex items-center justify-between px-3 py-2 text-left"
      >
        <div className="text-xs font-semibold uppercase tracking-wider text-[#0f1b2d]">
          Hotel / Accommodation
          {anySet ? <span className="ml-2 text-[10px] font-normal text-emerald-600">✓ set</span>
                  : <span className="ml-2 text-[10px] font-normal text-amber-600">— not entered</span>}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-[#475569]" /> : <ChevronDown className="w-4 h-4 text-[#475569]" />}
      </button>
      {expanded && (
        <div className="px-3 pb-3 space-y-2" onClick={(e) => e.stopPropagation()}>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#64748b]">Hotel name</label>
            <input
              type="text"
              value={values.hotel_name}
              onChange={(e) => setValues({ ...values, hotel_name: e.target.value })}
              data-testid={`hotel-name-${applicantIdx}`}
              className="w-full mt-0.5 border border-[#cbd5e1] rounded px-2 py-1 text-sm bg-white"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#64748b]">Hotel address</label>
            <textarea
              rows={2}
              value={values.hotel_address}
              onChange={(e) => setValues({ ...values, hotel_address: e.target.value })}
              data-testid={`hotel-address-${applicantIdx}`}
              className="w-full mt-0.5 border border-[#cbd5e1] rounded px-2 py-1 text-sm bg-white"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#64748b]">Hotel phone</label>
            <input
              type="text"
              value={values.hotel_phone}
              onChange={(e) => setValues({ ...values, hotel_phone: e.target.value })}
              data-testid={`hotel-phone-${applicantIdx}`}
              className="w-full mt-0.5 border border-[#cbd5e1] rounded px-2 py-1 text-sm bg-white"
            />
          </div>
          <button
            type="button"
            onClick={save}
            disabled={saving || !dirty}
            data-testid={`hotel-save-${applicantIdx}`}
            className="inline-flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5 rounded"
          >
            {saving ? "Saving…" : "Save hotel details"}
          </button>
        </div>
      )}
    </div>
  );
}

function ApplicantsSection({ applicants, numExpected, orderId, spainUsaEligible, onUpdate }) {
  const [openIdx, setOpenIdx] = useState(0);
  const [generatingIdx, setGeneratingIdx] = useState(null);
  const generateSpainVisa = async (idx) => {
    setGeneratingIdx(idx);
    try {
      const res = await api.post(
        `/admin/orders/${orderId}/generate-spain-visa-form?applicant_idx=${idx}`,
        null,
        { validateStatus: () => true },
      );
      if (res.status === 200) {
        const warnings = res.data?.warnings || [];
        if (warnings.length) {
          toast(`Saved to applicant's "Visa Application Form" slot.\nWarnings:\n• ${warnings.join("\n• ")}`, { duration: 9000, icon: "⚠️" });
        } else {
          toast.success("Saved to applicant's \"Visa Application Form\" slot.");
        }
        onUpdate && onUpdate();
      } else if (res.status === 422) {
        const missing = res.data?.missing || [];
        toast.error(`Cannot generate — ${missing.length} required field${missing.length === 1 ? "" : "s"} missing:\n• ${missing.join("\n• ")}`, { duration: 12000 });
      } else {
        toast.error(res.data?.detail || "PDF generation failed.");
      }
    } catch (e) {
      toast.error(`PDF generation failed: ${e.message || e}`);
    } finally {
      setGeneratingIdx(null);
    }
  };
  if (!applicants || applicants.length === 0) {
    return (
      <div className="bg-white border border-[#e2e8f0] rounded-md p-4 text-sm text-[#475569]">
        <span className="font-semibold text-[#0f1b2d]">Applicants:</span> none submitted yet
        {numExpected ? ` (${numExpected} expected)` : ""}.
      </div>
    );
  }
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-[#64748b] mb-2">
        Applicants ({applicants.length}{numExpected && numExpected !== applicants.length ? ` of ${numExpected}` : ""})
      </div>
      <div className="space-y-2" data-testid="admin-applicants-list">
        {applicants.map((a, i) => {
          const complete = applicantIsComplete(a);
          const open = openIdx === i;
          const generating = generatingIdx === i;
          return (
            <div key={i} className="bg-white border border-[#e2e8f0] rounded-md" data-testid={`admin-applicant-${i}`}>
              <button
                type="button"
                onClick={() => setOpenIdx(open ? null : i)}
                className="w-full flex items-center gap-3 p-4 text-left"
              >
                {complete ? <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" /> : <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-[#0f1b2d]">Applicant {i + 1} — {applicantDisplayName(a) || <span className="text-[#94a3b8] italic">(no name)</span>}</div>
                  <div className="text-xs text-[#64748b]">
                    {a.passport_number ? `Passport ${a.passport_number}` : "No passport"} · {a.nationality || "—"} · DOB {a.date_of_birth || "—"}
                  </div>
                </div>
                {open ? <ChevronUp className="w-4 h-4 text-[#475569]" /> : <ChevronDown className="w-4 h-4 text-[#475569]" />}
              </button>
              {spainUsaEligible && (
                <div className="px-4 pb-3 -mt-2 border-b border-[#f1f5f9] space-y-3">
                  <SpainHotelBlock
                    applicant={a}
                    orderId={orderId}
                    applicantIdx={i}
                    onUpdate={onUpdate}
                  />
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); generateSpainVisa(i); }}
                    disabled={generating}
                    data-testid={`generate-spain-visa-${i}`}
                    className="inline-flex items-center gap-2 bg-[#c9002b] hover:bg-[#a5001f] disabled:opacity-60 text-white text-xs font-semibold px-3 py-1.5 rounded"
                    title="Fill the official Spain Schengen visa application PDF and save it to this applicant's Visa Application Form slot"
                  >
                    <Download className="w-3.5 h-3.5" />
                    {generating ? "Generating…" : "Generate Spain Visa Application"}
                  </button>
                </div>
              )}
              {open && <ApplicantBreakdown a={a} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const ADDON_FIELD_LABELS = {
  noc: {
    job_title: "Job title", employment_start_date: "Employment start",
    signatory_name: "Signatory name", signatory_title: "Signatory title",
    employer_name: "Employer name", employer_address: "Employer address", employer_phone: "Employer phone",
  },
  sponsor_letter: {
    sponsor_name: "Sponsor name", relationship: "Relationship",
    occupation: "Occupation / income", address: "Address", phone: "Phone", email: "Email",
  },
  invitation_letter: {
    host_name: "Host name", host_address: "Host address",
    host_phone: "Host phone", host_email: "Host email", relationship: "Relationship",
    residency_status: "Residency status", purpose: "Purpose",
  },
};

function AddOnDetailsSection({ addOns, details }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-[#e2e8f0] rounded-lg overflow-hidden" data-testid="addon-details-section">
      <button onClick={() => setOpen((v) => !v)} className="w-full px-4 py-3 flex items-center justify-between bg-[#f8fafc] border-b border-[#e2e8f0]">
        <div className="text-sm font-semibold text-[#0f1b2d]">Add-on details ({addOns.length})</div>
        {open ? <ChevronUp className="w-4 h-4 text-[#475569]" /> : <ChevronDown className="w-4 h-4 text-[#475569]" />}
      </button>
      {open && (
        <div className="p-4 space-y-4">
          {addOns.map((id) => {
            const a = ADD_ONS_BY_ID[id]; if (!a) return null;
            const Icon = a.icon;
            const data = details?.[id] || {};
            const labels = ADDON_FIELD_LABELS[id] || {};
            return (
              <div key={id} className="border border-[#e2e8f0] rounded-md p-3" data-testid={`admin-addon-${id}`}>
                <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-[#0f1b2d]">
                  <Icon className="w-4 h-4 text-[#2563eb]" /> {a.name}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs">
                  {Object.entries(labels).map(([k, label]) => (
                    <div key={k} className="flex items-center justify-between gap-3 py-1 border-b border-[#f1f5f9]">
                      <span className="text-[#64748b]">{label}</span>
                      <span className="text-[#0f1b2d] font-medium text-right">{data[k] || <span className="text-[#94a3b8] italic">—</span>}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------- Customer form data — admin read-only view ---------- */
function CustomFormDataBlock({ order }) {
  const cfd = order.custom_form_data || {};
  const selected = order.selected_documents || [];
  const dash = "—";
  const show = (v) => (v === undefined || v === null || v === "" ? dash : v);

  const countryName = (code) => {
    if (!code) return dash;
    const c = worldCountryByCode(code);
    if (!c) return code;
    return (
      <span className="inline-flex items-center gap-1.5 align-middle">
        <Flag code={c.code} />
        <span>{c.name} ({code})</span>
      </span>
    );
  };

  const sections = [];

  // Contact & General — always shown
  sections.push(
    <FormSection key="contact" title="Contact & General">
      <Row label="Full name" value={show(cfd.full_name || order.full_name)} />
      <Row label="Email" value={show(cfd.email || order.email)} />
      <Row label="WhatsApp" value={show(cfd.whatsapp || order.whatsapp)} />
      <Row label="Applying from" value={countryName(cfd.applying_from_country || order.applying_from_country)} />
      <Row label="Destination" value={show(cfd.destination_country || order.destination_country)} />
      {(cfd.notes || order.notes) && <Row label="Notes" value={cfd.notes || order.notes} />}
    </FormSection>
  );

  const cfdTravellers = Array.isArray(cfd.travellers) ? cfd.travellers : [];
  if (cfdTravellers.length > 0) {
    sections.push(
      <FormSection key="travellers" title={`👥 Travellers (${cfdTravellers.length})`}>
        <div className="space-y-0.5">
          {cfdTravellers.map((t, i) => (
            <div key={i} className="text-[#0f1b2d]" data-testid={`admin-cfd-traveller-${i}`}>
              {i + 1}. <span className="font-semibold">{(t.name || "").trim() || `Traveller ${i + 1}`}</span> — {t.category || "Adult"}{t.dob ? <span className="text-[#64748b]"> (DOB: {t.dob})</span> : null}
            </div>
          ))}
        </div>
      </FormSection>
    );
  }

  if (selected.includes("flight") && cfd.flight) {
    const f = cfd.flight;
    sections.push(
      <FormSection key="flight" title="✈️ Flight Details">
        <Row label="Trip type" value={show(f.trip_type ? prettyTripType(f.trip_type) : "")} />
        {f.trip_type !== "multi" ? (
          <>
            <Row label="Departure" value={show(f.from)} />
            <Row label="Destination" value={show(f.to)} />
            <Row label="Departure date" value={show(f.departure_date)} />
            {f.trip_type === "round" && <Row label="Return date" value={show(f.return_date)} />}
          </>
        ) : (
          <div>
            <div className="text-[#64748b] mb-1">Legs</div>
            <ul className="space-y-0.5 ml-3">
              {(f.legs || []).map((leg, i) => (
                <li key={i} className="text-[#0f1b2d]">
                  Leg {i + 1}: {show(leg.from)} → {show(leg.to)} · {show(leg.date)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </FormSection>
    );
  }

  if (selected.includes("hotel") && cfd.hotel) {
    const h = cfd.hotel;
    const entries = Array.isArray(h.entries) ? h.entries : null;
    sections.push(
      <FormSection key="hotel" title="🏨 Hotel Details">
        {entries && entries.length > 0 ? (
          <div className="space-y-1">
            {entries.map((e, i) => (
              <div key={i} className="text-[#0f1b2d]" data-testid={`admin-cfd-hotel-entry-${i}`}>
                <span className="font-semibold">City {i + 1}:</span> {show(e.city)}
                <span className="text-[#64748b]"> · Check-in: </span>{show(e.check_in)}
                <span className="text-[#64748b]"> · Check-out: </span>{show(e.check_out)}
                <span className="text-[#64748b]"> · Preference: </span>{e.preferences || "—"}
              </div>
            ))}
          </div>
        ) : (
          <>
            <Row label="City / cities" value={show(h.cities)} />
            <Row label="Check-in" value={show(h.check_in)} />
            <Row label="Check-out" value={show(h.check_out)} />
            {h.preferences && <Row label="Preferences" value={h.preferences} />}
          </>
        )}
      </FormSection>
    );
  }

  if (selected.includes("car") && cfd.car) {
    const c = cfd.car;
    sections.push(
      <FormSection key="car" title="🚗 Car Rental Details">
        <Row label="Pick-up city" value={show(c.pickup_city)} />
        <Row label="Pick-up date" value={show(c.pickup_date)} />
        <Row label="Drop-off date" value={show(c.dropoff_date)} />
        <Row label="Driver name" value={show(c.driver_name)} />
      </FormSection>
    );
  }

  if (selected.includes("cover") && cfd.cover) {
    const c = cfd.cover;
    sections.push(
      <FormSection key="cover" title="📄 Cover Letter Details">
        <Row label="Purpose" value={show(c.purpose === "Other" && c.other_purpose ? `Other — ${c.other_purpose}` : c.purpose)} />
        <Row label="Employment status" value={show(c.employment_status)} />
        <Row label="Travel from" value={show(c.travel_from)} />
        <Row label="Travel to" value={show(c.travel_to)} />
      </FormSection>
    );
  }

  if (selected.includes("itinerary") && cfd.itinerary) {
    const it = cfd.itinerary;
    const itEntries = Array.isArray(it.entries) ? it.entries : null;
    const fmtShort = (iso) => {
      if (!iso) return "";
      const d = new Date(`${iso}T00:00:00`);
      return isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    };
    sections.push(
      <FormSection key="itinerary" title="🗺️ Itinerary Details">
        {itEntries && itEntries.length > 0 ? (
          <div className="space-y-0.5">
            {itEntries.map((e, i) => {
              const nights = e.from && e.to ? Math.max(0, Math.round((new Date(e.to) - new Date(e.from)) / 86400000)) : 0;
              const hasDates = e.date_mode === "custom" && e.from && e.to;
              return (
                <div key={i} className="text-[#0f1b2d]" data-testid={`admin-cfd-itin-city-${i}`}>
                  <span className="font-semibold">City {i + 1}:</span> {e.city || dash}
                  <span className="text-[#64748b]"> · </span>
                  {hasDates ? `${fmtShort(e.from)} – ${fmtShort(e.to)} (${nights} ${nights === 1 ? "night" : "nights"})` : "SchengenFile will arrange"}
                </div>
              );
            })}
          </div>
        ) : (
          <>
            <Row label="Cities to visit" value={show(it.cities)} />
            <Row label="Nights per city" value={show(it.nights)} />
          </>
        )}
        {it.activities && <Row label="Activities" value={it.activities} />}
      </FormSection>
    );
  }

  if (selected.includes("insurance") && cfd.insurance) {
    const dobs = Array.isArray(cfd.insurance.dobs) ? cfd.insurance.dobs : [];
    sections.push(
      <FormSection key="insurance" title="🛡️ Insurance Details">
        {dobs.length === 0 ? <Row label="DOBs" value={dash} /> : dobs.map((d, i) => (
          <Row key={i} label={`Applicant ${i + 1} DOB`} value={show(d)} />
        ))}
      </FormSection>
    );
  }

  // Express flag (optional context)
  if (cfd.isExpress) {
    sections.push(
      <FormSection key="delivery" title="⚡ Delivery">
        <Row label="Express delivery" value="Yes (3h)" />
      </FormSection>
    );
  }

  return (
    <div className="mt-3 pt-3 border-t border-dashed border-[#e2e8f0]" data-testid={`admin-custom-form-data-${order.id}`}>
      <div className="text-[10px] uppercase tracking-widest text-[#64748b] font-semibold mb-2">Customer Form Data</div>
      {sections.length === 1 ? (
        <div className="text-xs text-[#94a3b8] italic mb-2">No document-specific data submitted (customer may have paid before completing the form).</div>
      ) : null}
      <div className="space-y-3">{sections}</div>
    </div>
  );
}

function FormSection({ title, children }) {
  const slug = title.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();
  return (
    <div data-testid={`admin-cfd-section-${slug}`}>
      <div className="text-[11px] font-semibold text-[#2563eb] mb-1">{title}</div>
      <div className="space-y-0.5 ml-1">{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-[#64748b] text-[11px] min-w-[110px]">{label}:</span>
      <span className="text-[#0f1b2d]">{value}</span>
    </div>
  );
}

function prettyTripType(t) {
  if (t === "round") return "Round Trip";
  if (t === "oneway") return "One Way";
  if (t === "multi") return "Multi-City";
  return t;
}

